<script setup lang="ts">
import api from "@/api/api";
import { onMounted, onUnmounted, provide, ref, watch } from "@vue/runtime-core";

interface User {
  id: string;
  email: string;
  // Ajoutez d'autres propriétés utilisateur selon vos besoins
}

const props = defineProps<{
  user: User | null;
}>();

const user = ref<User | null>(props.user);
let heartbeatInterval: NodeJS.Timeout | null = null;

watch(
  () => props.user,
  (newUser) => {
    user.value = newUser;
    
    // Démarrer ou arrêter le heartbeat selon si l'utilisateur est connecté
    if (newUser && !heartbeatInterval) {
      startHeartbeat();
    } else if (!newUser && heartbeatInterval) {
      stopHeartbeat();
    }
  }
);

// Fonction pour envoyer un heartbeat au backend
const sendHeartbeat = async () => {
  if (!user.value) return;
  
  try {
    await api.post("/auth/track-connection");
  } catch (err) {
    console.error("Erreur lors du heartbeat:", err);
  }
};

// Démarrer le heartbeat (toutes les 60 secondes)
const startHeartbeat = () => {
  if (heartbeatInterval) return;
  
  heartbeatInterval = setInterval(() => {
    sendHeartbeat();
  }, 60 * 1000); // 60 secondes en millisecondes
};

// Arrêter le heartbeat
const stopHeartbeat = () => {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
};

// Gérer la fermeture de l'onglet/navigateur
const handleBeforeUnload = () => {
  if (!user.value) return;
  
  try {
    const token = localStorage.getItem("auth_token");
    if (token) {
      // Utiliser fetch synchrone avec keepalive pour garantir l'envoi
      fetch('/api/auth/track-disconnection', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({}),
        keepalive: true // Important : garde la requête vivante même si la page se ferme
      }).catch(err => {
        console.error("Erreur lors du tracking de fermeture:", err);
      });
    }
  } catch (err) {
    console.error("Erreur lors du tracking de fermeture:", err);
  }
};

// Gérer quand l'onglet devient invisible (changement d'onglet, minimisation, etc.)
const handleVisibilityChange = () => {
  if (!user.value) return;
  
  if (document.hidden) {
    // L'onglet est devenu invisible, arrêter le heartbeat
    stopHeartbeat();
  } else {
    // L'onglet est redevenu visible, redémarrer le heartbeat et envoyer une connexion
    if (user.value) {
      sendHeartbeat(); // Envoyer immédiatement un heartbeat
      startHeartbeat(); // Redémarrer le cycle
    }
  }
};

onMounted(() => {
  if (user.value) {
    startHeartbeat();
  }
  
  // Écouter l'événement de fermeture de page
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Écouter les changements de visibilité de l'onglet
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  stopHeartbeat();
  window.removeEventListener('beforeunload', handleBeforeUnload);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

provide("user", user);
</script>

<script lang="ts">
export default {
  name: "SessionProvider",
};
</script>

<template>
  <slot />
</template>
